import { useEffect, useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import View from "../atoms/view";
import { Label, P, SubTitle, TextRegular } from "../atoms/text";
import { WorkshopSession, DamageReport } from "../../types/workshopSession";
import { VehicleImage } from "./vehicleImage";
import { InfoSummary, InfoSummaryItem } from "./infoSummary";
import { SelectableList, SelectableListItem } from "./selectableList";
import { LightBox } from "./lightBox";
import theme from "../../theme";
import { Styles } from "../../constants/theme";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { Card } from "../atoms/card";

const formatLabel = (text = "") =>
  text
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatDate = (date?: string | null) => {
  if (!date) return "";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toISOString().slice(0, 10);
};

const getDamageTitle = (damage: DamageReport) => {
  const section = formatLabel(damage.section);
  const area = formatLabel(damage.area);
  return [section, area, "Damage"].filter(Boolean).join(" ");
};

const hasValue = (value?: string | number | null) =>
  value !== undefined && value !== null && String(value).trim() !== "";

export const DamageReports = ({
  vin,
  workSessionId,
  currentWorkshopSession,
}: {
  vin: string | undefined;
  workSessionId: string | undefined;
  currentWorkshopSession: WorkshopSession | null;
}) => {
  const damages = (currentWorkshopSession?.damageReports ?? []).filter(
    (d) => !workSessionId || d.workSessionId === workSessionId
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    setSelectedIndex(0);
    setImageIndex(0);
    setLightboxOpen(false);
  }, [currentWorkshopSession, workSessionId]);

  useEffect(() => {
    setImageIndex(0);
    setLightboxOpen(false);
  }, [selectedIndex]);

  const safeIndex = damages.length ? Math.min(selectedIndex, damages.length - 1) : 0;
  const selected = damages[safeIndex];
  const images = (selected?.images ?? []).filter((img) => hasValue(img.imageId));
  const safeImageIndex = images.length ? Math.min(imageIndex, images.length - 1) : 0;

  const selectDamage = (index: number) => {
    setSelectedIndex(index);
  };

  const listItems: SelectableListItem[] = damages.map((damage) => {
    const thumbId = damage.images?.find((img) => hasValue(img.imageId))?.imageId;
    const date = formatDate(damage.reportedAtLocal || damage.created);

    return {
      id: damage.damageId,
      title: getDamageTitle(damage),
      meta: [damage.process, damage.area ? formatLabel(damage.area) : null, date],
      thumbnail: thumbId ? (
        <VehicleImage blobId={thumbId} vehicleVin={vin} />
      ) : undefined,
    };
  });

  const metadataItems: InfoSummaryItem[] = selected
    ? [
      {
        icon: <AssignmentOutlinedIcon />,
        label: "Discovered during",
        value: selected.process,
      },
      {
        icon: <CalendarTodayOutlinedIcon />,
        label: "Date",
        value: formatDate(selected.reportedAtLocal || selected.created),
      },
      {
        icon: <PersonOutlineIcon />,
        label: "Inspector",
        value: undefined,
      },
      {
        icon: <LocalOfferOutlinedIcon />,
        label: "Location",
        value: selected.area ? formatLabel(selected.area) : "",
      },
      {
        icon: <ImageOutlinedIcon />,
        label: "Images",
        value: images.length,
      },
    ].filter((item) => hasValue(item.value))
    : [];

  const detailRows = selected
    ? [
      { label: "Location", value: selected.area ? formatLabel(selected.area) : "" },
      { label: "Notes", value: selected.description },
    ].filter((row) => hasValue(row.value))
    : [];

  return (
    <>
      {!damages.length || !selected ? (
        <View extend={styles.empty}>
          <P>No damages reported</P>
        </View>
      ) : (
        <View extend={styles.layout}>
          <SelectableList
            title={`REPORTED DAMAGES (${damages.length})`}
            items={listItems}
            selectedIndex={safeIndex}
            onSelect={selectDamage}
          />

          <View extend={styles.detailPanel}>
            <View extend={styles.titleRow}>
              <View extend={styles.detailTitle}>{getDamageTitle(selected)}</View>
            </View>

            {metadataItems.length ? <InfoSummary items={metadataItems} /> : null}

            <View extend={styles.photoSection}>
              <View extend={styles.photoColumn}>
                {images.length ? (
                  <>
                    <View
                      extend={[styles.largeImage, styles.largeImageClickable]}
                      onClick={() => setLightboxOpen(true)}
                    >
                      <VehicleImage
                        blobId={images[safeImageIndex].imageId}
                        vehicleVin={vin}
                      />
                    </View>
                    {lightboxOpen ? (
                      <LightBox
                        imageIds={images
                          .map((img) => img.imageId)
                          .filter((id): id is string => Boolean(id))}
                        vehicleVin={vin}
                        open={lightboxOpen}
                        initialIndex={safeImageIndex}
                        onClose={() => setLightboxOpen(false)}
                        onIndexChange={setImageIndex}
                      />
                    ) : null}
                    {images.length > 1 ? (
                      <View extend={styles.imageNav}>
                        <View
                          as="button"
                          type="button"
                          extend={[
                            styles.navButton,
                            safeImageIndex === 0 && styles.navButtonDisabled,
                          ]}
                          disabled={safeImageIndex === 0}
                          onClick={() =>
                            safeImageIndex > 0 && setImageIndex(safeImageIndex - 1)
                          }
                        >
                          <KeyboardArrowLeftIcon fontSize="small" />
                          Previous
                        </View>
                        <View extend={styles.imageCounter}>
                          {safeImageIndex + 1} / {images.length}
                        </View>
                        <View
                          as="button"
                          type="button"
                          extend={[
                            styles.navButton,
                            safeImageIndex >= images.length - 1 && styles.navButtonDisabled,
                          ]}
                          disabled={safeImageIndex >= images.length - 1}
                          onClick={() =>
                            safeImageIndex < images.length - 1 &&
                            setImageIndex(safeImageIndex + 1)
                          }
                        >
                          Next
                          <KeyboardArrowRightIcon fontSize="small" />
                        </View>
                      </View>
                    ) : null}
                  </>
                ) : (
                  <P>No damage photos</P>
                )}
              </View>

              {detailRows.length ? (
                <Card>
                  <SubTitle noPadding extend={styles.sectionLabel}>DETAILS</SubTitle>
                  {detailRows.map((row, index) => (
                    <View
                      key={row.label}
                      extend={[
                        styles.detailField,
                        index < detailRows.length - 1 && styles.detailFieldBorder,
                      ]}
                    >
                      <Label noPadding >{row.label}</Label>
                      <TextRegular noPadding>{row.value}</TextRegular>
                    </View>
                  ))}
                </Card>
              ) : null}
            </View>
          </View>
        </View>
      )}
    </>
  );
};

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: "1fr 1.6fr",
    gap: "24px",
    width: "100%",
    alignItems: "start",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  empty: {
    padding: "24px",
  },
  detailPanel: {
    minWidth: "0",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  navButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    fontFamily: "Futura",
    fontSize: "13px",
    color: theme.colors.darkGrey,
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: `${Styles.BorderRadius}px`,
    padding: "6px 12px",
    cursor: "pointer",
  },
  navButtonDisabled: {
    opacity: "0.4",
    cursor: "default",
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  detailTitle: {
    fontFamily: "Futura",
    fontSize: "22px",
    fontWeight: 600,
    color: theme.colors.darkGrey,
  },
  photoSection: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr",
    gap: "16px",
    alignItems: "start",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  photoColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: "0",
  },
  sectionLabel: {
    fontFamily: "Futura",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    color: theme.colors.darkGrey,
    marginBottom: "4px",
  },
  largeImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    overflow: "hidden",
    borderRadius: `${Styles.BorderRadius}px`,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.lightGrey,
    position: "relative",
    "> *": {
      position: "absolute",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      width: "100%",
      height: "100%",
    },
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
      display: "block",
    },
  },
  largeImageClickable: {
    cursor: "pointer",
  },
  imageNav: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  imageCounter: {
    fontFamily: "Futura",
    fontSize: "13px",
    color: theme.colors.grey,
  },
  detailsCard: {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: `${Styles.BorderRadius}px`,
    padding: "16px",
    minWidth: "0",
  },
  detailField: {
    paddingTop: "12px",
    paddingBottom: "12px",
  },
  detailFieldBorder: {
    borderBottom: `1px solid ${theme.colors.border}`,
  }
};
